/**
 * SelettorePartita — menu a tendina in cima al dettaglio partita per
 * passare a un'altra partita della stessa giornata (Andrea 2026-09-20).
 *
 * Mentre si seguono i voti live si vogliono vedere anche le altre sfide
 * senza tornare ogni volta al calendario. Le partite sono quelle della
 * giornata in TUTTE le competizioni (campionato, coppa), raggruppate per
 * competizione; quella aperta e' evidenziata e non cliccabile.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogoSquadra } from "../common/LogoSquadra";
import type { CompetizioneItem, PartitaItem } from "../../hooks/useFase5";
import { tr } from "../../i18n";

interface Props {
  /** La partita aperta adesso. */
  partita: PartitaItem;
  /** Tutte le partite della giornata (ogni competizione). */
  partite: PartitaItem[] | undefined;
  /** Competizioni della stagione, per i nomi dei gruppi. */
  competizioni: CompetizioneItem[] | undefined;
  nomeSquadra: (id: string) => string;
  /** Numero di giornata, per la riga di intestazione. */
  giornata: number | null;
}

/** "2 - 1" a giornata calcolata, "vs" prima. */
function risultato(p: PartitaItem): string {
  return p.punteggio_casa !== null && p.punteggio_casa !== undefined
    ? `${p.gol_casa} - ${p.gol_trasferta}`
    : "vs";
}

export function SelettorePartita({ partita, partite, competizioni, nomeSquadra, giornata }: Props) {
  const navigate = useNavigate();
  const [aperto, setAperto] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Chiusura con Esc o toccando fuori dal menu.
  useEffect(() => {
    if (!aperto) return;
    const onTasto = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAperto(false);
    };
    const onFuori = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setAperto(false);
    };
    document.addEventListener("keydown", onTasto);
    document.addEventListener("mousedown", onFuori);
    return () => {
      document.removeEventListener("keydown", onTasto);
      document.removeEventListener("mousedown", onFuori);
    };
  }, [aperto]);

  const altre = (partite ?? []).filter((p) => !p.obsoleto);
  if (altre.length <= 1) return null;

  const nomeCompetizione = (id: string): string =>
    competizioni?.find((c) => c.id === id)?.nome ?? tr("Altre partite");
  // Un gruppo per competizione, nell'ordine in cui compaiono; la
  // competizione della partita aperta per prima.
  const gruppi: Array<{ id: string; nome: string; partite: PartitaItem[] }> = [];
  for (const p of altre) {
    let g = gruppi.find((x) => x.id === p.competizione_id);
    if (!g) {
      g = { id: p.competizione_id, nome: nomeCompetizione(p.competizione_id), partite: [] };
      gruppi.push(g);
    }
    g.partite.push(p);
  }
  gruppi.sort((a, b) =>
    a.id === partita.competizione_id ? -1 : b.id === partita.competizione_id ? 1 : 0,
  );

  const vaiA = (p: PartitaItem) => {
    setAperto(false);
    if (p.id !== partita.id) navigate(`/partita/${p.id}`);
  };

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        aria-expanded={aperto}
        aria-haspopup="listbox"
        className="w-full flex items-center justify-between gap-2 rounded-card border border-primary/30 bg-bg-card px-3 py-2 text-left hover:bg-bg-card-hover transition"
        title={tr("Vedi il live delle altre partite della giornata")}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-base leading-none">⚽</span>
          <span className="min-w-0">
            <span className="block font-mono text-[9px] uppercase tracking-wider text-fg-muted">
              {giornata !== null ? tr("Giornata {{v1}}", { v1: giornata }) : tr("Giornata")} · altre
              partite
            </span>
            <span className="block text-sm text-fg truncate">
              {nomeSquadra(partita.squadra_casa_id)}
              <span className="text-fg-muted"> - </span>
              {nomeSquadra(partita.squadra_trasferta_id)}
            </span>
          </span>
        </span>
        <span
          aria-hidden="true"
          className={`shrink-0 text-accent text-xs transition-transform ${aperto ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>

      {aperto && (
        <div
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-1 max-h-[60vh] overflow-y-auto rounded-card border border-primary/30 bg-bg-card shadow-xl"
        >
          {gruppi.map((g) => (
            <div key={g.id}>
              <div className="px-3 py-1.5 bg-primary-deep/40 font-mono text-[9px] uppercase tracking-wider text-fg-muted sticky top-0">
                {g.nome}
              </div>
              {g.partite.map((p) => {
                const corrente = p.id === partita.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="option"
                    aria-selected={corrente}
                    onClick={() => vaiA(p)}
                    className={`w-full grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 py-2 border-t border-primary/10 text-left transition ${
                      corrente ? "bg-accent/10" : "hover:bg-bg-card-hover"
                    }`}
                  >
                    <span className="flex items-center gap-1.5 min-w-0">
                      <LogoSquadra squadraId={p.squadra_casa_id} size={20} />
                      <span className="text-[12px] truncate">{nomeSquadra(p.squadra_casa_id)}</span>
                    </span>
                    <span
                      className={`font-mono text-[12px] tabular-nums px-1 ${
                        corrente ? "text-accent font-bold" : "text-fg-muted"
                      }`}
                    >
                      {risultato(p)}
                    </span>
                    <span className="flex items-center justify-end gap-1.5 min-w-0">
                      <span className="text-[12px] truncate text-right">
                        {nomeSquadra(p.squadra_trasferta_id)}
                      </span>
                      <LogoSquadra squadraId={p.squadra_trasferta_id} size={20} />
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
